import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppState, ACTION_TYPES } from '../hooks/useAppState';
import { getTheme } from '../lib/theme';
import { Check } from 'lucide-react';

const EDADES = ['15 – 19','20 – 24','25 – 29','30 – 34','35 – 39','40 – 44','45 – 49','50 – 54','55 – 59','60 – 64','65 – 69','70 o más'];
const TIEMPOS = ['Menos de 6 meses','Entre 6 meses y 1 año','Entre 1 a 4 años','Entre 5 a 9 años','Entre 10 a 14 años','Entre 15 a 19 años','Entre 20 a 24 años','25 años o más'];

export default function FichaPage() {
  const { state, dispatch } = useAppState();
  const navigate = useNavigate();
  const t = getTheme(state.darkMode);
  const dark = state.darkMode;

  const trab = state.trabajadorActual;
  if (!trab) { navigate('/cuestionarios'); return null; }

  const [ficha, setFicha] = useState({
    sexo: '', edad: '', estadoCivil: '', nivelEstudios: '',
    puesto: '', area: '', tipoPuesto: '', tipoContratacion: '',
    tipoPersonal: '', tipoJornada: '', rotacionTurnos: '',
    tiempoPuesto: '', tiempoExperiencia: '',
  });

  const camposLlenos = ficha.sexo && ficha.edad && ficha.puesto && ficha.area && ficha.tipoPuesto && ficha.tipoContratacion && ficha.tipoJornada;

  const guardar = () => {
    dispatch({ type: ACTION_TYPES.SET_FICHA, payload: { centroIdx: state.centroActualIdx, trabajadorId: trab.id, ficha: { ...ficha } } });
    navigate('/cuestionarios');
  };

  const inputStyle = { width: '100%', padding: '10px 14px', boxSizing: 'border-box', background: t.bgInput, border: `1px solid ${t.borderInput}`, borderRadius: 8, color: t.textStrong, fontSize: '0.85rem', outline: 'none' };
  const labelStyle = { display: 'block', fontSize: '0.72rem', fontWeight: 600, color: t.textMuted, marginBottom: 4 };

  const Select = ({ label, value, onChange, options, required }) => (
    <div>
      <label style={labelStyle}>{label}{required && ' *'}</label>
      <select style={inputStyle} value={value} onChange={e => onChange(e.target.value)}>
        <option value="">Seleccionar</option>
        {options.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );

  return (
    <div style={{ maxWidth: 720, margin: '0 auto', padding: '40px 24px' }}>
      <h2 style={{ fontSize: '1.4rem', fontWeight: 700, color: t.textStrong, margin: 0 }}>Ficha de datos del trabajador</h2>
      <p style={{ color: t.textMuted, fontSize: '0.88rem', marginBottom: 20 }}>Guía de Referencia V · {trab.nombreCompleto || trab.nombre}</p>

      <div style={{ padding: '12px 20px', background: 'rgba(245,158,11,0.08)', borderRadius: 10, marginBottom: 20, fontSize: '0.82rem', color: '#f59e0b' }}>
        Esta información es confidencial y se usa exclusivamente para fines de mejora del ambiente de trabajo, conforme a la NOM-035-STPS-2018.
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {/* Sexo y Edad */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Select label="Sexo" value={ficha.sexo} onChange={v => setFicha(p => ({ ...p, sexo: v }))} options={['Masculino', 'Femenino']} required />
          <Select label="Edad (años)" value={ficha.edad} onChange={v => setFicha(p => ({ ...p, edad: v }))} options={EDADES} required />
        </div>

        <Select label="Estado civil" value={ficha.estadoCivil} onChange={v => setFicha(p => ({ ...p, estadoCivil: v }))} options={['Soltero(a)', 'Casado(a)', 'Unión libre', 'Divorciado(a)', 'Viudo(a)']} />

        <Select label="Nivel de estudios" value={ficha.nivelEstudios} onChange={v => setFicha(p => ({ ...p, nivelEstudios: v }))}
          options={['Sin formación', 'Primaria incompleta', 'Primaria terminada', 'Secundaria incompleta', 'Secundaria terminada', 'Preparatoria o bachillerato incompleta', 'Preparatoria o bachillerato terminada', 'Técnico superior incompleta', 'Técnico superior terminada', 'Licenciatura incompleta', 'Licenciatura terminada', 'Maestría incompleta', 'Maestría terminada', 'Doctorado incompleto', 'Doctorado terminado']} />

        {/* Puesto y Área */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div>
            <label style={labelStyle}>Ocupación / Puesto *</label>
            <input style={inputStyle} placeholder="Ej. Analista contable" value={ficha.puesto} onChange={e => setFicha(p => ({ ...p, puesto: e.target.value }))} />
          </div>
          <div>
            <label style={labelStyle}>Departamento / Área *</label>
            <input style={inputStyle} placeholder="Ej. Finanzas" value={ficha.area} onChange={e => setFicha(p => ({ ...p, area: e.target.value }))} />
          </div>
        </div>

        {/* Tipo puesto y contratación */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Select label="Tipo de puesto" value={ficha.tipoPuesto} onChange={v => setFicha(p => ({ ...p, tipoPuesto: v }))} options={['Operativo', 'Profesional o técnico', 'Supervisor', 'Gerente']} required />
          <Select label="Tipo de contratación" value={ficha.tipoContratacion} onChange={v => setFicha(p => ({ ...p, tipoContratacion: v }))} options={['Por obra o proyecto', 'Por tiempo determinado (temporal)', 'Tiempo indeterminado', 'Honorarios']} required />
        </div>

        {/* Personal y jornada */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Select label="Tipo de personal" value={ficha.tipoPersonal} onChange={v => setFicha(p => ({ ...p, tipoPersonal: v }))} options={['Sindicalizado', 'Confianza', 'Ninguno']} />
          <Select label="Tipo de jornada" value={ficha.tipoJornada} onChange={v => setFicha(p => ({ ...p, tipoJornada: v }))} options={['Fijo diurno (entre las 6:00 y 20:00 hrs)', 'Fijo nocturno (entre las 20:00 y 6:00 hrs)', 'Fijo mixto (combinación de nocturno y diurno)']} required />
        </div>

        <Select label="Rotación de turnos" value={ficha.rotacionTurnos} onChange={v => setFicha(p => ({ ...p, rotacionTurnos: v }))} options={['Sí', 'No']} />

        {/* Experiencia */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Select label="Tiempo en el puesto actual" value={ficha.tiempoPuesto} onChange={v => setFicha(p => ({ ...p, tiempoPuesto: v }))} options={TIEMPOS} />
          <Select label="Experiencia laboral total" value={ficha.tiempoExperiencia} onChange={v => setFicha(p => ({ ...p, tiempoExperiencia: v }))} options={TIEMPOS} />
        </div>
      </div>

      {/* Footer */}
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 32, paddingTop: 20, borderTop: `1px solid ${t.border}` }}>
        <button onClick={() => navigate('/cuestionarios')} style={{ padding: '12px 24px', background: 'none', border: `1px solid ${t.borderInput}`, borderRadius: 10, color: t.textMuted, fontSize: '0.9rem', cursor: 'pointer' }}>Cancelar</button>
        <button disabled={!camposLlenos} onClick={guardar} style={{ padding: '12px 24px', background: t.accent, color: '#fff', border: 'none', borderRadius: 10, fontSize: '0.9rem', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, opacity: camposLlenos ? 1 : 0.5 }}>
          Guardar ficha <Check size={18} />
        </button>
      </div>
    </div>
  );
}
